
import { Observable, ReplaySubject } from 'rx';
import parseToken from '../bastetjs/utils/parseToken';

import Configure from './Configure';
import FetchApi from './FetchApi';
import eventBus from '../event-bus-rx';

import { guid } from './utils';

const AUTH_TOKEN = 'AUTH_TOKEN';

var tokenStream = Observable.never();
var activityStream = Observable.never();

function _getAccessTokenUrl() {
  const url = _keycloakURL(
    Configure.getConfigs().keycloakUrlBase,
    Configure.getConfigs().keycloakRealm,
    'protocol/openid-connect/token');
  return url;
}

function _redirectUri(options) {
  if (options && options.redirectUri) {
    return options.redirectUri;
  } else {
    let redirectUri = location.href;
    if (location.hash) {
      redirectUri = redirectUri.substring(0, location.href.indexOf('#'));
      redirectUri += (redirectUri.indexOf('?') == -1 ? '?' : '&') + 'redirect_fragment=' + encodeURIComponent(location.hash.substring(1));
    }
    return redirectUri;
  }
}

function _createLoginUrl(options, join) {
  const state = guid();
  const nonce = guid();

  let redirectUri = _redirectUri(options);
  if (options && options.prompt) {
      redirectUri += (redirectUri.indexOf('?') == -1 ? '?' : '&') + 'prompt=' + options.prompt;
  }

  Configure.setConfigs({
    keycloakOAuthState: {
      state,
      nonce,
      redirectUri,
    }
  });

  const realm = Configure.getConfigs().keycloakRealm;
  const clientId = Configure.getConfigs().keycloakClientId;
  const responseMode = Configure.getConfigs().keycloakResponseMode;
  const responseType = Configure.getConfigs().keycloakResponseType;

  const protocol = join
     ? 'protocol/openid-connect/registrations'
     : 'protocol/openid-connect/auth';

  let url = _keycloakURL(Configure.getConfigs().keycloakUrlBase, realm, protocol)
      + '?client_id=' + encodeURIComponent(clientId)
      + '&redirect_uri=' + encodeURIComponent(redirectUri)
      + '&state=' + encodeURIComponent(state)
      + '&nonce=' + encodeURIComponent(nonce)
      + '&response_mode=' + encodeURIComponent(responseMode)
      + '&response_type=' + encodeURIComponent(responseType);

  return url;
}

function _assembleUserRequest(body) {
  const keycloakClientId = Configure.getConfigs().keycloakClientId
  const keycloakSecret = Configure.getConfigs().keycloakSecret

  return {
    method: 'post',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: body
      + '&client_id=' + encodeURIComponent(keycloakClientId)
      + '&client_secret=' + encodeURIComponent(keycloakSecret)
  }
}

function _fetchToken(url, req, errorCallback) {
  const err = typeof errorCallback === 'function'
    ? errorCallback : (_) => console.log(`Error fetching token: ${_}`);

  return Observable
    .just({ token: false, isLoading: true })
    .concat(
      FetchApi.doFetch(url, req)
        .do(
          (resp) => {
            Configure.setConfigs({
              access_token: resp.access_token,
              refresh_token: resp.refresh_token
            });
          },
          err
        )
        .map((resp) => {
          return {
            token: resp,
            isLoading: false
          };
        })
    )
    .take(2)
    .forEach((data) => eventBus.publish(AUTH_TOKEN, data), err)
}

function _logout(redirectUri) {
  const realm = Configure.getConfigs().keycloakRealm;
  const logoutUri = _keycloakURL(Configure.getConfigs().keycloakUrlBase, realm, 'protocol/openid-connect/logout')
                  + (redirectUri ? '?redirectUri=' + encodeURIComponent(redirectUri) : '');
  //console.log(logoutUri);
  return FetchApi.doFetch(logoutUri, null, false);
}

function _fetchAuthCodeToken(code, redirectUri, errorCallback) {
  const body = 'grant_type=authorization_code'
    + '&code=' + code
    + '&redirect_uri='+ encodeURIComponent(redirectUri);

  return _fetchToken(_getAccessTokenUrl(), _assembleUserRequest(body), errorCallback);
}

function _fetchDirectGrantToken(username, password) {
  const body = 'grant_type=password'
    + '&username=' + username
    + '&password=' + password;

  return _fetchToken(_getAccessTokenUrl(), _assembleUserRequest(body));
}

function _setAuthCode(code, errorCallback) {
  const redirectUri = Configure.getConfigs().keycloakRedirectUri;
  _fetchAuthCodeToken(code, redirectUri, errorCallback);
}

function _getAuthToken() {
  const startWith = Configure.getConfigs().access_token
    ? { token: Configure.getConfigs().access_token }
    : { token: false };

  return Observable
    .just(startWith)
    .concat(
      eventBus.getTopic(AUTH_TOKEN)
    );
}

function _expiresIn(token)	{
  return (_decodeToken(token).exp * 1000) - _epochTime();
}

function _hasExpired(token)	{
  return _expiresIn(token) <= 0;
}

function _decodeToken(tok) {
  const parsed = parseToken.parse(tok);
  _check(parsed, ['exp'], 'decodeToken');
  return parsed;
}

function _epochTime() {
  return (new Date).getTime();
}

function _keycloakURL(urlBase, realm, suffixPath) {
  return urlBase + '/auth/realms/' + realm + '/' + suffixPath
}

function _fetchRefreshToken(urlBase, realm, params = {}, useProxy = true) {
  try {
    const refreshToken = Configure.getConfigs().refresh_token;

    if (params.client_secret || !useProxy) {
      params.grant_type = 'refresh_token';
      params.refresh_token = refreshToken;
      return FetchApi.doFetch(_keycloakURL(urlBase, realm, 'protocol/openid-connect/token'), _toPostRequest(params));
    }
    else {
      const hostUrl = Configure.getConfigs().hostUrl;
      const decoded = parseToken.parse(refreshToken || '');
      const url = `${hostUrl}/auth/token/refresh/${realm}/${decoded.aud || params.client_id}`;
      return FetchApi.doFetch(url,
        {
          method: 'post',
          headers: {
            'Content-Type': 'text/plain'
          },
          body: Configure.getConfigs().refresh_token
        });
    }
  }
  catch(err) {

    return Observable.empty();
  }
}

function _tap(obs) {
  obs.do((resp) => {

  });
}

function _check(obj, params, context) {
  const missing =
    params.filter((key) => !obj.hasOwnProperty(key));

  if (missing.length) {
    throw new Error(missing.join(', ') + ' is/are missing in ' + context);
  }
}

function _toPostRequest(params) {
  if (params.refresh_token) {
    _check(params, [ 'client_id' ], 'toPostRequest');
    return {
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: _toQueryString(params)
    }
  } else {
    throw new Error('refresh_token is not set, unable to generate a toPostRequest');
  }
}

function _toQueryString(obj) {
  return Object.keys(obj).reduce(function(acc, k) {
    acc.push(encodeURIComponent(k) + '=' + encodeURIComponent(obj[k]))
    return acc;
  }, []).join('&');
};

function _refreshTokenScheduler(refreshInterval, scheduler) {
  return Observable.interval(refreshInterval, scheduler).timeInterval();
}

function _activity(timestampHolder, requireActivity) {
  return requireActivity ? timestampHolder && timestampHolder.timestamp : _epochTime();
}

function _startTokenRefreshScheduler(scheduler) {

  _check(Configure.getConfigs(), [ 'tokenRefreshInterval', 'keycloakUrlBase', 'keycloakRealm', 'keycloakClientId', 'requireActivity' ], 'startTokenRefreshScheduler');

  const tokenRefreshInterval = Configure.getConfigs().tokenRefreshInterval;
  const requireActivity = Configure.getConfigs().requireActivity;

  return Observable.combineLatest(
    activityStream,
    _refreshTokenScheduler(tokenRefreshInterval, scheduler))
//     .do((msg) => { console.log(showTick(scheduler) + ' msg: ', msg); })
     .map((combined) => { return _activity(combined[0], requireActivity); })
//     .do((lastKnownActivity) => { console.log('lkg: ' + lastKnownActivity + ' remaining: ' + (epochTime()-lastKnownActivity) + '  interval: ' + tokenRefreshInterval); })
     .filter((lastKnownActivity) => { return lastKnownActivity && ((_epochTime()-lastKnownActivity) <= tokenRefreshInterval); })
//     .do(() => { console.log('worthy of a refresh!' ); } )
     .flatMap((params) => {
        return _fetchRefreshTokenUsingConfigs(Configure.getConfigs());
       })
     //.do((refreshedToken) => { console.log(showTick(scheduler) + 'refreshedToken: ', refreshedToken ); } )
  ;
}

function _fetchRefreshedToken(configs, errorCallback) {
  const err = typeof errorCallback === 'function'
    ? errorCallback : (_) => console.log(`Fetch refreshed token error: ${_}`);

  const stream = _fetchRefreshTokenUsingConfigs(configs).do(
    (msg) => {
      if (msg) {
        Configure.setConfigs({
          access_token: msg.access_token,
          refresh_token: msg.refresh_token
        });
      }
    },
    err
  );
  stream.subscribe(
    (msg) => eventBus.publish(AUTH_TOKEN, {token: msg, isLoading: false}),
    err
  );
  return stream;
}

function _fetchRefreshTokenUsingConfigs(configs) {
  const keycloakUrlBase = configs.keycloakUrlBase;
  const keycloakRealm = configs.keycloakRealm;
  const keycloakClientId = configs.keycloakClientId;
  const keycloakSecret = configs.keycloakSecret;
  const useProxy = configs.useProxy;

  return _fetchRefreshToken(
    keycloakUrlBase,
    keycloakRealm, {
      'client_id': keycloakClientId,
      'client_secret': keycloakSecret
    },
  useProxy);
}

function _showTick(scheduler) {
  return (scheduler ? 'tick(' + scheduler.clock + ') ' : '');
}

function _activateThruConfigs(scheduler, errorCallback) {
  const access_token = Configure.getConfigs().access_token;
  const refresh_token = Configure.getConfigs().refresh_token;
  return _activate({ access_token, refresh_token }, scheduler, errorCallback);
}

function _activate({ access_token, refresh_token }, scheduler, errorCallback) {
  const err = typeof errorCallback === 'function'
    ? errorCallback : (_) => console.log(`Error in token stream: ${_}`);

  tokenStream = new ReplaySubject(1, null, scheduler);
  tokenStream.subscribe(
    (token)   => {
      Configure.setConfigs({
        access_token: token.access_token,
        refresh_token: token.refresh_token
      });
    },
    err,
    () => { if (done) done() }
  );

  activityStream = new ReplaySubject(1, null, scheduler);
  activityStream.onNext({ msg: 'messaging activated' });

  _startTokenRefreshScheduler(scheduler)
    .subscribe(
      (msg) => {
        const newToken = Array.isArray(msg) ? msg[0] : msg;
        // console.log('updating tokenStream with: ', newToken);
        tokenStream.onNext(newToken);
      });

  return { activityStream, tokenStream };
}

function _getServiceAccountToken() {

    const keycloakClientId = Configure.getConfigs().keycloakClientId;
    const keycloakSecret = Configure.getConfigs().keycloakSecret;

    if (keycloakClientId && keycloakSecret) {
      const basicAuth = btoa(`${keycloakClientId}:${keycloakSecret}`);
      const req = {
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${basicAuth}`
        },
        body: 'grant_type=client_credentials'
      };
      return _fetchToken(_getAccessTokenUrl(), req);
    }
    else {
      const hostUrl = Configure.getConfigs().hostUrl;
      const realm = Configure.getConfigs().keycloakRealm;

      return _fetchToken(`${hostUrl}/auth/token/service-account/${realm}/${keycloakClientId}`,
        {
          method: 'post',
        });
    }
}

function _connectFacebookToApp(fbUserId, fbUserName){
  const keycloakToken = Configure.getConfigs().access_token;

  const req = {
    method: 'post',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Bearer ${keycloakToken}`
    },
    body: _toQueryString({
      identityProvider: 'facebook',
      userId: fbUserId,
      userName: fbUserName
    })
  };

  return _connectSocialMedia(req);
}

function _connectGoogleToApp(googleUserId, googleUserName){
  const keycloakToken = Configure.getConfigs().access_token;

  const req = {
    method: 'post',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Bearer ${keycloakToken}`
    },
    body: _toQueryString({
      identityProvider: 'google',
      userId: googleUserId,
      userName: googleUserName
    })
  };

  return _connectSocialMedia(req);
}

function _connectSocialMedia(req) {

  const urlBase = Configure.getConfigs().ATHLINKS_API_HOST;
  const connectUrl = `${urlBase}/account/api/AddSocialConnection`;

  return Observable
    .just({ isLoading: true })
    .concat(FetchApi.doFetch(connectUrl, req))
}

const KeycloakApi = {
  createLoginUrl: _createLoginUrl,
  fetchAuthCodeToken: _fetchAuthCodeToken,
  fetchDirectGrantToken: _fetchDirectGrantToken,
  fetchRefreshedToken: _fetchRefreshedToken,
  getAuthToken: _getAuthToken,
  setAuthCode: _setAuthCode,
  fetchRefreshToken: _fetchRefreshToken,
  toPostRequest: _toPostRequest,
  decodeToken: _decodeToken,
  epochTime: _epochTime,
  expiresIn: _expiresIn,
  refreshTokenScheduler: _refreshTokenScheduler,
  activate: _activate,
  activateThruConfigs: _activateThruConfigs,
  startTokenRefreshScheduler: _startTokenRefreshScheduler,
  getServiceAccountToken: _getServiceAccountToken,
  fetchRefreshTokenUsingConfigs: _fetchRefreshTokenUsingConfigs,
  connectFacebookToApp: _connectFacebookToApp,
  connectGoogleToApp: _connectGoogleToApp,
  logout: _logout
};

export default KeycloakApi;



/** WEBPACK FOOTER **
 ** ./lib/anuket-http/KeycloakApi.js
 **/